import { Children, FC, PropsWithChildren, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Column, Row, Stack } from 'src/components-v2/Layout';
import { Indicator } from 'src/components-v3/Indicator/Indicator';
import type { DecorativeStatSectionShape } from 'src/types/contentful';

type Props = PropsWithChildren<{
  appearance: DecorativeStatSectionShape['appearance'];
  mobileAppearance?: DecorativeStatSectionShape['mobileAppearance'];
}>;

const DecorativeStatStepper: FC<Props> = ({
  children,
  appearance,
  mobileAppearance,
}) => {
  const childrenAsArray = Children.toArray(children);
  const childrenCount = childrenAsArray.length;

  const [activeIndex, setActiveIndex] = useState(0);

  const isMobileStacked = mobileAppearance === 'Stacked';
  const verticalMobileRowGap = appearance === 'Small' ? '3.2rem' : '4.8rem';

  const handleClick = (index: number) => {
    setActiveIndex(index);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      handleClick(Math.min(activeIndex + 1, childrenCount - 1));
    },
    onSwipedRight: () => {
      handleClick(Math.max(activeIndex - 1, 0));
    },
    delta: 10,
    preventScrollOnSwipe: !isMobileStacked,
    swipeDuration: 1_000,
    touchEventOptions: { passive: true },
    trackTouch: !isMobileStacked,
  });

  return (
    <Row>
      <Column
        xs={isMobileStacked ? 22 : 24}
        xsOffset={isMobileStacked ? 1 : 0}
        sm={24}
        smOffset={0}
      >
        <Stack gap={4}>
          <Row
            component='ul'
            ref={handlers.ref}
            columnSpacing={{
              sm: 2,
              md: childrenCount > 3 ? 4 : 10.625,
            }}
            sx={{
              flexDirection: {
                xs: isMobileStacked ? 'column' : 'row',
                sm: 'row',
              },
              rowGap: {
                xs: isMobileStacked ? verticalMobileRowGap : 0,
                sm: 0,
              },
              transform: {
                xs: isMobileStacked
                  ? 'none'
                  : `translateX(calc(-100% * 22 / var(--Grid-columns) * ${activeIndex}))`,
                sm: 'none',
              },
              transition: {
                xs: isMobileStacked ? 'none' : 'transform 0.3s ease-in-out',
                sm: 'none',
              },
              listStyle: 'none',
              flexWrap: 'nowrap',
              alignItems: {
                xs: isMobileStacked ? 'stretch' : 'center',
                sm: 'center',
              },
            }}
            {...handlers.onMouseDown}
          >
            {childrenAsArray.map((card, i) => {
              return (
                <Column
                  key={`card-${i}`}
                  component='li'
                  sx={{
                    minWidth: {
                      xs: isMobileStacked
                        ? '100%'
                        : 'calc(100% * 21 / var(--Grid-columns))',
                      sm: 0,
                    },
                    flexGrow: 1,
                  }}
                  xs={24}
                  xsOffset={isMobileStacked ? 0 : 1}
                  smOffset={0}
                >
                  {card}
                </Column>
              );
            })}
          </Row>

          <Row
            sx={{
              display: {
                xs: isMobileStacked ? 'none' : 'flex',
                sm: 'none',
              },
            }}
          >
            <Column xs={24}>
              <Stack justifyContent='center' flexDirection='row' gap={0.75}>
                {childrenAsArray.map((_, i) => {
                  return (
                    <Indicator
                      key={`indicator-${i}`}
                      isActive={activeIndex === i}
                      index={i}
                      onClick={handleClick}
                    />
                  );
                })}
              </Stack>
            </Column>
          </Row>
        </Stack>
      </Column>
    </Row>
  );
};

export default DecorativeStatStepper;
